'use client';

import type { ReactNode } from 'react';
import { useCallback, useMemo, useRef } from 'react';

import { generateRandomString, LinkedList } from '@repo/common';
import { SDSToastProvider } from '@repo/sds/SDSToast';

import { ThemeWrapper } from '../components/containers/ThemeWrapper/ThemeWrapper';
import { PortalType, UiContext } from './ui-context';

const EMPTY_FEATURE_FLAGS: string[] = [];

interface UiProviderProps {
  children: ReactNode;
  featureFlags?: string[];
  featureFlagsLoading?: boolean;
}

export function UiProvider({
  children,
  featureFlags = EMPTY_FEATURE_FLAGS,
  featureFlagsLoading = false,
}: UiProviderProps) {
  const linkedListRef = useRef<LinkedList<HTMLDivElement> | null>(null);
  if (linkedListRef.current === null) {
    linkedListRef.current = new LinkedList();
  }
  const portalList = linkedListRef.current;

  const registerUiPortal = useCallback(
    (type: PortalType) => {
      const id = generateRandomString(5);
      const currentPortal = document.createElement('div');

      const tail = portalList.tail;

      const existsZIndex =
        tail && !Number.isNaN(Number(tail.value.style.zIndex)) ? Number(tail.value.style.zIndex) : 2500;
      const newZIndex = type === PortalType.Modal ? existsZIndex + 100 : existsZIndex + 1;

      currentPortal.id = id;
      currentPortal.style.zIndex = String(newZIndex);
      currentPortal.dataset.type = type;
      portalList.add(id, currentPortal);
      document.body.appendChild(currentPortal);

      return currentPortal;
    },
    [portalList],
  );

  const removeUiPortal = useCallback(
    (id: string) => {
      const removedPortalNode = portalList.remove(id);
      document.body.removeChild(removedPortalNode.value);
    },
    [portalList],
  );

  const value = useMemo(
    () => ({ registerUiPortal, removeUiPortal, featureFlags, featureFlagsLoading }),
    [registerUiPortal, removeUiPortal, featureFlags, featureFlagsLoading],
  );

  return (
    <UiContext.Provider value={value}>
      <ThemeWrapper>
        <SDSToastProvider>{children}</SDSToastProvider>
      </ThemeWrapper>
    </UiContext.Provider>
  );
}
